import React, { createContext, useContext } from 'react';
import type { PosFiscalBuyerDraft } from '../../../fiscal/domain-api';
import type { CartPromoUiStatus } from '../../application/posCartPromoPresentation'
import type { PosTipIntent } from '../../application/posCheckoutTip';
import type { PosCheckoutCustomerOption, PosDraftPayment, PosFiscalMode, PosPaymentMethodId, PosPaymentMethodRow, PosStockWarningDetail } from '../../domain/posTypes';
import type { CartItem } from '../../store/usePosStore';
import type { CartEvaluationResult } from '../../../promotions/public-api';

export type PosCheckoutContextValue = {
  cashSessionOpen: boolean | null;
  onOpenCashManagement?: () => void;
  cashManagementAccessBlockedHint?: string;
  total: number;
  merchandiseTotal?: number;
  tipAmount?: number;
  tipUiEnabled?: boolean;
  tipIntent?: PosTipIntent | null;
  onSelectTipIntent?: (next: PosTipIntent | null) => void;
  cart?: CartItem[];
  cartEvaluation?: CartEvaluationResult | null;
  promoUiStatus?: CartPromoUiStatus;
  saleFiscalMode: PosFiscalMode;
  onSetSaleFiscalMode: (mode: PosFiscalMode) => void;
  allowFiscalSaleMode: boolean;
  stockWarningsLoading: boolean;
  stockCanProceed: boolean;
  stockWarningDetails?: PosStockWarningDetail[];
  hasBlockingStockIssue: boolean;
  hasExpiredStock?: boolean;
  paymentDrafts: PosDraftPayment[];
  paymentMethods: PosPaymentMethodRow[];
  customerEligibleCustomers: PosCheckoutCustomerOption[];
  customersTotal: number;
  customerPageIndex: number;
  customerPageSize: number;
  onLoadMoreCustomers: (pageIndex: number, loadAll?: boolean) => void;
  saleCustomerId: string | null;
  setSaleCustomerId: (id: string | null) => void;
  totalIngresado: number;
  saldoPendiente: number;
  vuelto: number;
  isSaving: boolean;
  onUpdateDraft: (id: string, patch: Partial<PosDraftPayment>) => void;
  onRemoveDraft: (id: string) => void;
  onAddSplitPayment: (methodId?: PosPaymentMethodId) => void;
  onCompleteSale: () => void;
  onRequestStockCorrection: () => void;
  fiscalBuyerDraft?: PosFiscalBuyerDraft;
  onFiscalBuyerDraftChange?: (patch: Partial<PosFiscalBuyerDraft>) => void;
  currentBranchId?: string | null;
  /** Comisión: asignar performed_by / sold_by a una línea del carrito */
  onSetCartLineAttribution?: (lineId: string, performedByUserId: string | null, soldByUserId: string | null) => void;
};

export type PosCheckoutModalProps = {
  visible: boolean;
  checkoutLoading: boolean;
  onClose: () => void;
  onRequestClose?: () => void;
  /** Overlays (ej. confirm de drift) que deben pintar dentro del Modal. */
  overlayModal?: React.ReactNode;
};

const PosCheckoutContext = createContext<PosCheckoutContextValue | null>(null);

export function PosCheckoutProvider({
  value,
  children,
}: {
  value: PosCheckoutContextValue;
  children: React.ReactNode;
}) {
  return <PosCheckoutContext.Provider value={value}>{children}</PosCheckoutContext.Provider>;
}

export function usePosCheckoutContextOptional() {
  return useContext(PosCheckoutContext);
}
